import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  ReactNode,
} from 'react'
import type {
  IFilterData,
  IFilterDataArray,
} from '@rankmi/rankmi-table/dist/interfaces/filterProps'

import { canSkipOnRowSelected } from 'helpers'
import { Segment } from 'models/segment.model'
import { SortValue } from 'models/rankmiTable.model'
import SegmentsTable from 'components/configuration/components/lms-profiles/SegmentsTable'
import {
  listSegments,
  ListSegmentsParams,
} from 'api/administration/segments/list'

interface TableProps {
  users: number[]
  onRemove: (index: number) => void
  selected: number[]
  setSelected: React.Dispatch<React.SetStateAction<number[]>>
  actions?: ReactNode
}

const Table = ({
  users,
  onRemove,
  selected,
  setSelected,
  actions,
}: TableProps) => {
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<SortValue>()
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)

  const selectedUsersMap = useMemo(() => new Set(selected), [selected])

  const [loading, setLoading] = useState(false)
  const [list, setList] = useState<Segment[]>([])
  const [total, setTotal] = useState(0)

  const [filterData, setFilterData] = useState<IFilterData>()

  useEffect(() => {
    setPage(1)
  }, [users])

  const filter = useMemo(
    (): IFilterDataArray[] => [
      {
        input: 'id',
        condition: 'and',
        operator: 'in',
        // @ts-ignore TODO: fix this in @rankmi/rankmi-table
        compareTo: users as IFilterDataArray['compareTo'],
      },
    ],
    [users]
  )

  const filterDataMerge = useMemo(
    () => ({
      filterType: 'simple',
      ...filterData,
      data: [...(filterData?.data || []), ...(filter || [])],
    }),
    [filterData, filter]
  )

  const params = useMemo(
    (): Omit<ListSegmentsParams, 'page' | 'perPage'> => ({
      searchText: search,
      sort,
      filterData: filterDataMerge,
    }),
    [search, sort, filterDataMerge]
  )

  useEffect(() => {
    if (!users.length) {
      setList([])
      setTotal(0)
      return
    }
    setLoading(true)
    listSegments({
      page,
      perPage,
      ...params,
    })
      .then(({ data, total }) => {
        setList(data)
        setTotal(total)
      })
      .finally(() => setLoading(false))
  }, [users, page, params, perPage])

  const handleSearch = (value: string) => {
    setSearch(value)
    setPage(1)
  }

  const handleAllSelected = useCallback(async () => {
    setSelected(users)
  }, [setSelected, users])

  const handleAllSelectedRef = useRef(handleAllSelected)

  useEffect(() => {
    handleAllSelectedRef.current = handleAllSelected
  }, [handleAllSelected])

  return (
    <div className="position-relative">
      <SegmentsTable
        data={{
          list,
          total,
          loading,
        }}
        onRemove={(participant, index) => {
          const newSelected = new Set(selected)
          newSelected.delete(participant.id)
          setSelected(Array.from(newSelected))
          onRemove(index)
        }}
        onRowSelected={({ selectedRows, selectedRowKeys, dispatcher }) => {
          if (canSkipOnRowSelected(dispatcher)) return

          if (dispatcher === 'handleUnselectAllRows') {
            setSelected([])
            return
          }
          const newUsers = selectedRows.filter(
            ({ id }) => !selectedUsersMap.has(id)
          ) as Segment[]

          const keysSet = new Set(selectedRowKeys)

          const deletedUsers = users.filter((id) => !keysSet.has(id))

          // Rankmi-table has a bug, use onAllRowKeys how reference
          if (newUsers.length) {
            setSelected((prev) => [...prev, ...newUsers.map((user) => user.id)])
          }

          if (deletedUsers.length) {
            setSelected((prev) => {
              const deletedUsersMap = new Set(deletedUsers)
              return prev.filter((id) => !deletedUsersMap.has(id))
            })
          }
        }}
        selected={selected}
        onAllRowKeys={handleAllSelected}
        paginationConfig={{
          setSort,
          page,
          perPage,
          onChangeFilterData: setFilterData,
          setPage,
          setPerPage,
          filterData,
          search,
          onSearch: handleSearch,
        }}
        actions={actions}
      />
    </div>
  )
}

export default Table
